// Forces a stale client onto the current build.
//
// Hashed assets are served `immutable, max-age=31536000`, so a client holding a cached
// index.html from an earlier deploy can run entirely out of its own cache and never learn that a
// newer build exists. Production has sent `no-store` on the HTML since 2026-05-07, which means a
// single uncached document request heals such a client permanently — the problem is that some
// clients go months without making one.
//
// Two triggers cover that:
//   1. Vite's `vite:preloadError`, fired when a lazy chunk is no longer on the server. With the
//      server now answering 404 for a missing /assets/<hash> (instead of falling back to
//      index.html) this fires reliably.
//   2. An explicit version comparison on load and whenever the tab returns to the foreground.
//      The foreground check is the important one for iOS home-screen installs, which can stay
//      suspended for weeks without ever re-requesting the document.
//
// Every path reloads at most once per tab session, so a genuinely broken deploy or a
// permanently missing chunk cannot produce a reload loop.

const RELOAD_FLAG = 'versionGuardReloaded';
const PENDING_REPORT = 'versionGuardPendingReport';

// Don't re-check on every single tab focus; a tab-switcher would otherwise issue a request per
// switch. The staleness we are hunting is measured in days, so minutes of slack cost nothing.
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Replaced at build time from package.json (see vite.config.js). Must stay quoted — the
// replacement is a raw token substitution, not a JSON one. Informational only: it is bumped by
// hand and has been unchanged across ~20 commits, so it cannot identify a build.
const RUNNING_VERSION = '__VERSION__';

// The build identity we actually compare. This module is bundled into the entry chunk, so
// import.meta.url is that chunk's URL and its content hash changes on every code change —
// exactly the property `version` lacks.
const RUNNING_ENTRY = (() => {
    try {
        return import.meta.url.split('/').pop() || null;
    } catch {
        return null;
    }
})();

let lastCheckedAt = 0;

function report(level, message) {
    if (typeof Rollbar === 'undefined') return;

    Rollbar[level](message);
}

function alreadyReloaded() {
    try {
        return sessionStorage.getItem(RELOAD_FLAG) === '1';
    } catch {
        // Private-mode Safari can throw on sessionStorage access. Treat it as "already
        // reloaded" so we never risk looping when we cannot record that we tried.
        return true;
    }
}

// Returns true when a reload was actually triggered, so callers can tell whether the condition
// they detected has been handled or merely swallowed.
function reloadOnce(reason) {
    if (alreadyReloaded()) return false;

    // Never interrupt a payment that is already in flight.
    if (window.location.pathname.startsWith('/buyPack')) return false;

    try {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        // Hand the diagnostic to the *next* page load rather than logging it here: Rollbar
        // batches and sends asynchronously, so a payload queued immediately before
        // location.reload() is almost always aborted before it leaves the browser.
        sessionStorage.setItem(PENDING_REPORT, `${reason}|${RUNNING_ENTRY}`);
    } catch {
        return false;
    }

    window.location.reload();

    return true;
}

// Emits the diagnostic for a reload performed by the previous page load, where there is no
// teardown race to lose it to.
function reportPendingReload() {
    let pending;

    try {
        pending = sessionStorage.getItem(PENDING_REPORT);
        if (pending) sessionStorage.removeItem(PENDING_REPORT);
    } catch {
        return;
    }

    if (!pending) return;

    const [reason, from] = pending.split('|');

    report('info', `Version guard reloaded (${reason}): ${from} -> ${RUNNING_ENTRY} (v${RUNNING_VERSION})`);
}

async function deployedBuild() {
    // `cache: 'no-store'` bypasses the HTTP cache, which matters because the whole point is that
    // this client's cache is not to be trusted.
    const response = await fetch('/version.json', { cache: 'no-store' });

    if (!response.ok) return null;

    return await response.json();
}

function clearReloadFlag() {
    try {
        sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
        // Nothing to do — the flag only exists to prevent loops.
    }
}

async function checkVersion({ force = false } = {}) {
    const now = Date.now();

    if (!force && now - lastCheckedAt < CHECK_INTERVAL_MS) return;

    lastCheckedAt = now;

    try {
        const deployed = await deployedBuild();

        if (!deployed?.entry || !RUNNING_ENTRY) return;

        if (deployed.entry === RUNNING_ENTRY) {
            // Confirmed current. Release the once-per-session latch so a LATER deploy can still
            // heal this tab: an iOS home-screen install keeps one session alive for weeks, and
            // leaving the latch set would pin it to this build until the app is force-quit.
            clearReloadFlag();

            return;
        }

        reloadOnce(`entry ${deployed.entry} deployed, running ${RUNNING_ENTRY}`);
    } catch {
        // Offline, or the request failed — nothing to do, we simply keep running.
    }
}

export function installVersionGuard() {
    reportPendingReload();

    window.addEventListener('vite:preloadError', (event) => {
        // The event is cancelable; preventDefault stops Vite rethrowing, and the reload is the
        // recovery. But if we decline to reload we have just silenced the only signal that this
        // client is broken, so report it instead — that is the #2163 case still happening.
        event.preventDefault();

        if (!reloadOnce('chunk load failed')) {
            const detail = event.payload?.message || event.payload || 'unknown';

            report('error', `Chunk load failed, reload declined (running ${RUNNING_VERSION}): ${detail}`);
        }
    });

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkVersion();
    });

    checkVersion({ force: true });
}
